/**
 * "Menções para mim" (#569): quem chamou esta pessoa numa nota interna.
 *
 * O card da menção some em segundos, como o de mensagem. Quem estava no meio
 * de um atendimento quando alguém pediu "@Maria pode aprovar?" precisa achar
 * o chamado depois — e é para isso que esta lista existe: as menções dos
 * últimos 30 dias, com as não lidas primeiro.
 *
 * Mora no menu, como um "@" ao lado do sino do Atendimento, e só aparece
 * quando há menção. Era uma linha inteira embaixo do item; com o sino e ela,
 * a linha do Atendimento quebrava no trilho estreito (30/09/2026). O número
 * de não lidas fica no canto do ícone. Clicar numa menção da lista marca como
 * vista e abre a conversa.
 */

import type { ReactNode } from 'react';
import type { MencoesParaMim } from '@cliqcrm/contracts';
import { Botao, Dialogo, Icone } from '@/shared/ui';
import { useMarcarNotificacoesVistas, useMencoesParaMim } from '@/shared/api/consultas';
import { idadeEmPalavras } from '@/shared/ui/estados/idade';
import { mensagemDaNotificacao } from '../lib/texto-da-notificacao';
import './mencoes-para-mim.css';

type Mencao = MencoesParaMim['itens'][number];

/**
 * O "@" no menu: só o botão, com as não lidas no canto.
 *
 * O diálogo NÃO mora aqui (30/09/2026). O botão fica num grupo do menu com
 * `transform`, e um ancestral com `transform` vira a referência do
 * `position: fixed` — o diálogo abria espremido dentro do menu, e não no
 * centro da tela. E o "@" só existe com o menu aberto: dentro dele, recolher
 * o menu fecharia o diálogo junto. Por isso ele é `DialogoDeMencoesParaMim`,
 * desenhado pela casca, fora do menu.
 */
export function MencoesParaMimNoMenu({ aoAbrir }: { aoAbrir: () => void }): ReactNode {
  const consulta = useMencoesParaMim(true);
  const itens = consulta.data?.itens ?? [];
  if (itens.length === 0) return null;
  const naoLidas = itens.filter((m) => !m.vista).length;
  const rotulo =
    naoLidas > 0
      ? `Menções para mim, ${naoLidas === 1 ? '1 não lida' : `${naoLidas} não lidas`}`
      : 'Menções para mim';

  return (
    <button
      type="button"
      className="mencoes-para-mim"
      onClick={aoAbrir}
      aria-label={rotulo}
      title={rotulo}
    >
      <span className="mencoes-para-mim__icone" aria-hidden="true">
        <Icone nome="arroba" />
        {naoLidas > 0 && (
          <span className="mencoes-para-mim__numero">{naoLidas > 9 ? '9+' : naoLidas}</span>
        )}
      </span>
    </button>
  );
}

/**
 * A lista de menções, no centro da tela. Clicar numa marca como vista e abre a
 * conversa; as já vistas ficam na lista com o selo "Visto".
 */
export function DialogoDeMencoesParaMim({
  aoFechar,
  aoAbrirConversa,
}: {
  aoFechar: () => void;
  aoAbrirConversa: (conversaId: string) => void;
}): ReactNode {
  const consulta = useMencoesParaMim(true);
  const marcar = useMarcarNotificacoesVistas();
  const itens = consulta.data?.itens ?? [];

  const abrir = (mencao: Mencao): void => {
    if (!mencao.vista) marcar.mutate([mencao.id]);
    aoFechar();
    aoAbrirConversa(mencao.conversa_id);
  };

  return (
    <Dialogo
      titulo="Menções para mim"
      aoFechar={aoFechar}
      botoes={<Botao onClick={aoFechar}>Fechar</Botao>}
    >
      <p className="mencoes-para-mim__apoio">
        Quem chamou você numa nota interna nos últimos 30 dias. As não lidas vêm primeiro.
      </p>
      {itens.length === 0 ? (
        <p className="mencoes-para-mim__apoio" role="status">
          Nenhuma menção nos últimos 30 dias.
        </p>
      ) : (
        <ul className="mencoes-para-mim__lista">
          {itens.map((mencao) => (
            <li key={mencao.id}>
              <button
                type="button"
                className="mencoes-para-mim__item"
                data-lida={mencao.vista ? 'sim' : undefined}
                onClick={() => abrir(mencao)}
              >
                <span className="mencoes-para-mim__quem">
                  {!mencao.vista && <span className="visualmente-oculto">Não lida. </span>}
                  <strong>{mencao.autor?.trim() || 'Alguém da equipe'}</strong> mencionou você
                  {mencao.cliente.nome_exibicao
                    ? ` na conversa com ${mencao.cliente.nome_exibicao}`
                    : ''}
                </span>
                <span className="mencoes-para-mim__trecho">{mensagemDaNotificacao(mencao)}</span>
                <span className="mencoes-para-mim__rodape">
                  <span className="mencoes-para-mim__quando">
                    {idadeEmPalavras(mencao.criada_em)}
                  </span>
                  {mencao.vista && <span className="mencoes-para-mim__visto">Visto</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialogo>
  );
}
